package com.kinoshazam.uiKit.components.image

import androidx.compose.foundation.Image
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.layout.ContentScale
import coil3.compose.rememberAsyncImagePainter
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.placeholder
import org.jetbrains.compose.resources.painterResource

@Composable
fun UiKitImage(
    state: ImageUiKitState,
    modifier: Modifier = Modifier,
    alignment: Alignment = Alignment.Center,
) {
    val hasUrl = !state.url.isNullOrBlank()
    val fallbackPainter = state.placeholder ?: if (hasUrl) {
        painterResource(Res.drawable.placeholder)
    } else {
        null
    }
    val fallbackFilter = state.placeholderColor?.let { ColorFilter.tint(color = it) }

    if (!hasUrl) {
        fallbackPainter?.let { painter ->
            Image(
                painter = painter,
                contentDescription = state.contentDescription,
                colorFilter = fallbackFilter,
                contentScale = ContentScale.Fit,
                alignment = alignment,
                modifier = modifier,
            )
        }
        return
    }

    var imageLoadResult by remember(state.url) {
        mutableStateOf<Result<Painter>?>(null)
    }
    val painter = rememberAsyncImagePainter(
        model = state.url,
        onSuccess = {
            imageLoadResult =
                if (it.painter.intrinsicSize.width > 1 && it.painter.intrinsicSize.height > 1) {
                    Result.success(it.painter)
                } else {
                    Result.failure(Exception("Invalid image size"))
                }
        },
        onError = {
            it.result.throwable.printStackTrace()
            imageLoadResult = Result.failure(it.result.throwable)
        }
    )

    when (val result = imageLoadResult) {
        null -> {
            // Loading: same treatment as error — Fit so icons aren't cropped into odd shapes.
            fallbackPainter?.let { placeholder ->
                Image(
                    painter = placeholder,
                    contentDescription = state.contentDescription,
                    colorFilter = fallbackFilter,
                    contentScale = ContentScale.Fit,
                    alignment = alignment,
                    modifier = modifier,
                )
            }
        }

        else -> {
            val success = result.isSuccess
            Image(
                painter = if (success) painter else (fallbackPainter ?: painter),
                colorFilter = if (success) state.tintColor else fallbackFilter,
                contentDescription = state.contentDescription,
                contentScale = if (success) state.contentScale else ContentScale.Fit,
                alignment = alignment,
                modifier = modifier,
            )
        }
    }
}
